import React, { useEffect, useMemo, useState } from 'react'
import { ActivityIndicator } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { useB2C } from '@zet/common/hooks/useB2C'
import { useAgentDetailsWF } from '@zet/customer'
import { BTN_SM, colors as zetColors } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { sendAnalyticsData } from '@zet/integration-flow/utils/analyticsEvents'
import { i18n } from '@zet/localization'
import { IBrandListRoute, IWebApplyButton } from '@zet/utils/interface'

import { useFontFlowStore } from '../../../store/fontFlowStore'
import {
  useBrandDetailsById,
  useGenerateSharableLink,
} from '../../api/brands/hooks'
import { useApplyLink } from '../../hooks/useApplyLink'
import {
  MAGNET_CARD_PARTNER_ID,
  NAVIGATE_TO_MAGNET_CARD_SCREEN_WEBVIEW_EVENT,
} from '../../utils/constants'
import UserConsent from '../userConsent'

const WebApplyButton = ({
  partnerId,
  alreadyApplied,
  isSourceWeb,
  isInEligible,
  customerId,
  index,
  customerPhoneNumber,
  leadAdded,
  customerName,
  setIsBottomSheetVisible,
  setIsExploreOtherCardsBSVisible,
  variantId,
  isApplySbmFromBottomSheet,
}: IWebApplyButton) => {
  const { isJfsFlow } = useFontFlowStore()
  const {
    mutate: fetchShareLink,
    isLoading,
    isSuccess,
    data: shareLinkData,
  } = useGenerateSharableLink()
  const { data: AgentData } = useAgentDetailsWF()
  const { data: content } = useBrandDetailsById(partnerId)
  const [consentBottomSheetVisible, setConsentBottomSheetVisible] =
    useState(false)
  const route = useRoute()
  const params: IBrandListRoute = route.params as IBrandListRoute
  const { isB2C, showIneligible } = useB2C()

  const openBottomSheet = () => {
    setConsentBottomSheetVisible(true)
  }
  const closeBottomSheet = () => {
    setConsentBottomSheetVisible(false)
  }

  function handleLeadSource() {
    // Applying from the rejection view (rejection reason on top, other
    // brands below) attributes the lead to the rejection flow.
    if (params?.rejectedBrandName) {
      return 'reject-page-web'
    } else if (isB2C) {
      return 'B2C'
    } else if (route?.name === 'CrossSelling') {
      return 'cross-web'
    } else if (isSourceWeb) {
      return 'webBrand'
    } else {
      return 'web'
    }
  }
  const { handleApply } = useApplyLink(
    shareLinkData,
    partnerId,
    params?.customerName || customerName,
    customerId,
    customerPhoneNumber,
    isSourceWeb,
    openBottomSheet,
    closeBottomSheet,
    false,
    isJfsFlow,
    variantId
  )

  const showIneligibleCard = showIneligible && isInEligible && isB2C
  const isDsaEligible = AgentData?.isDsa && isInEligible


  useEffect(() => {
    if (isSuccess && shareLinkData) {
      if (isJfsFlow) {
        sendAnalyticsData({
          screen: 'SHOW_QUICK_RECOMMENDATION_SCREEN',
          type: 'click',
        })
      }
      handleApply()
      setIsBottomSheetVisible && setIsBottomSheetVisible(false)
    }
  }, [isSuccess, shareLinkData])

  const buttomStyle = useMemo(() => {
    if (isJfsFlow) {
      return 'bg-primary-100 rounded-3xl'
    } else if (showIneligibleCard) {
      return 'bg-primary-100 rounded-md'
    } else if (!leadAdded && (isDsaEligible || alreadyApplied)) {
      return 'border-primary-100 border rounded-md'
    } else if (isApplySbmFromBottomSheet) {
      return 'bg-[#691195]'
    } else {
      return 'bg-primary-100 rounded-md'
    }
  }, [])

  const buttonTextStyle = useMemo(() => {
    if (isJfsFlow) {
      return 'text-tertiary-100'
    } else if (showIneligibleCard) {
      return 'text-neutral-white100'
    } else if (!leadAdded && (isDsaEligible || alreadyApplied)) {
      return 'text-primary-100'
    } else {
      return 'text-neutral-white100'
    }
  }, [])

  const buttonText = () => {
    if (showIneligibleCard) {
      return 'brandPage.knowMore'
    } else if (leadAdded) {
      return 'brandPage.continue'
    } else if (isDsaEligible) {
      return 'brandPage.applyAnyway'
    } else if (alreadyApplied) {
      return 'brandPage.reapply'
    } else if (isInEligible) {
      return 'recommendationBottomSheet.sellAnywayCTA'
    } else {
      return 'brandPage.applyNow'
    }
  }

  const payloadForButton = useMemo(() => {
    if (showIneligibleCard) {
      return {
        item_name: 'add_customer_applyAnyway',
        customer_id: customerId,
        brand_name: content?.name,
        brand_category: content?.subCategory,
        eligibility: 'no',
        recommendation_position: index + 1,
      }
    }

    if (leadAdded) {
      return {
        item_name: 'add_customer_apply_continue',
        customer_id: customerId,
        brand_name: content?.name,
        brand_category: content?.subCategory,
        eligibility: 'yes',
        recommendation_position: index + 1,
      }
    }

    return alreadyApplied
      ? {
          item_name: 'add_customer_applyAnyway',
          customer_id: customerId,
          brand_name: content?.name,
          brand_category: content?.subCategory,
          eligibility: 'yes',
          recommendation_position: index + 1,
        }
      : {
          item_name: 'add_customer_apply',
          customer_id: customerId,
          brand_name: content?.name,
          brand_category: content?.subCategory,
          eligibility: 'yes',
          recommendation_position: index + 1,
        }
  }, [content, customerId, index])

  const handlePressForWeb = () => {
    if (showIneligibleCard) {
      setIsExploreOtherCardsBSVisible?.(true)
      return
    }

    if (isB2C && Number(partnerId) === MAGNET_CARD_PARTNER_ID) {
      window?.ReactNativeWebView?.postMessage(
        NAVIGATE_TO_MAGNET_CARD_SCREEN_WEBVIEW_EVENT
      )
      return
    }

    fetchShareLink({
      partnerId: Number(partnerId),
      customerId: customerId,
      leadSource: handleLeadSource(),
      brandSpecificPartnerId: params?.brandSpecificPartnerId,
      variantId,
    })
    return
  }

  // if (!(AgentData?.isDsa || (false && isB2C))) {
  //   return <></>
  // }

  return (
    <>
      <Pressable
        className={`${
          isJfsFlow ? 'mt-1' : 'mt-3'
        } flex-1 items-center justify-center  py-2 ${buttomStyle}`}
        onPress={handlePressForWeb}
        eventName="click_action"
        eventPayload={payloadForButton}
        disabled={isLoading}
      >
        {isLoading ? (
          <ActivityIndicator color={zetColors.neutral.white100} />
        ) : (
          <BTN_SM className={`text-center ${buttonTextStyle}`}>
            {i18n.t(buttonText())}
          </BTN_SM>
        )}
      </Pressable>
      {consentBottomSheetVisible && (
        <UserConsent
          brandId={partnerId}
          customerId={customerId}
          customerName={customerName}
          leadSource={handleLeadSource()}
          customerPhoneNumber={customerPhoneNumber}
          isBottomSheetVisible={consentBottomSheetVisible}
          setIsBottomSheetVisible={setConsentBottomSheetVisible}
        />
      )}
    </>
  )
}

export default WebApplyButton
